'use client';

import React, { useCallback, useEffect, useState } from 'react';
import BaseModal from 'src/components/primitives/BaseModal/BaseModal';
import useWalletsToDisplay from 'src/hooks/useWalletsToDisplay';
import { VelocityWalletName } from 'src/constants/wallets';
import {
	getHistoricallySelectedWallets,
	HISTORICALLY_SELECTED_WALLETS_STORAGE_KEY,
} from 'src/hooks/useWalletsToDisplay';
import useHandleSelectWalletProvider from 'src/hooks/useHandleSelectWalletProvider';
import useHasAcknowledgedTerms from 'src/hooks/useHasAcknowledgedTerms';
import useWalletIsConnected from 'src/hooks/useWalletIsConnected';
import useCurrentWalletConnectionState from 'src/hooks/useCurrentWalletConnectionState';
import useHasConnectedWalletBefore from 'src/hooks/useHasConnectedWalletBefore';
import { useAccountFetchedAndExists } from 'src/hooks/user/useAccountFetchedAndExists';
import { MainWalletsPage } from './MainWalletsPage';
import { MoreWalletsPage } from './MoreWalletsPage';
import useModal from 'src/components/Modals/hooks/useModal';
import PrivyEmailOtpPage from 'src/components/Modals/Onboarding/ConnectWalletModal/PrivyEmailOtpPage/PrivyEmailOtpPage';
import usePrivyEmailOtpResendTimeout from 'src/components/Modals/Onboarding/ConnectWalletModal/PrivyEmailOtpPage/hooks/usePrivyEmailOtpResendTimeout';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';
import { usePrivateBetaWalletWhitelistStatus } from 'src/hooks/privateBetaWalletWhitelist/usePrivateBetaWalletWhitelistStatus';
import { clearPrivateBetaWalletRejected } from 'src/hooks/privateBetaWalletWhitelist/privateBetaWalletWhitelistState';
import { cj } from 'src/utils/classNames';

const CONNECTION_STEP_LOCK_KEY = 'ONBOARDING_CONNECTING_LOCK';
const MAX_DEFAULT_WALLETS_TO_SHOW = 2;

export interface ConnectWalletModalProps {
	callback?: () => void;
}

export default function ConnectWalletModal({
	callback,
}: ConnectWalletModalProps) {
	const walletConnectionState = useCurrentWalletConnectionState();
	const connected = useWalletIsConnected();
	const isMobileScreenSize = useIsMobileScreenSize();
	const { show, hide, canGoBack, goBack } = useModal();

	const { otpResendSecondsRemaining, setIsOtpSent } =
		usePrivyEmailOtpResendTimeout();

	const [hasAcknowledgedTerms] = useHasAcknowledgedTerms();
	const [hasAccountsLoaded, accountExists] = useAccountFetchedAndExists();
	const { hasAccess, isLoading, isPrivateBetaWhitelistEnabled } =
		usePrivateBetaWalletWhitelistStatus();

	const [privyEmail, setPrivyEmail] = useState('');

	const { handleSelectProvider } = useHandleSelectWalletProvider();
	const [_, setHasConnectedWalletBefore] = useHasConnectedWalletBefore();

	const {
		walletsToShow,
		hiddenWallets,
		hiddenDetectedWallets,
		hiddenNonDetectedWallets,
	} = useWalletsToDisplay(MAX_DEFAULT_WALLETS_TO_SHOW);

	const [isPrivyEmailAuth, setIsPrivyEmailAuth] = useState(false);
	const [showMoreWallets, setShowMoreWallets] = useState(false);

	const handleAddToHistoricallySelectedWallets = useCallback((name: string) => {
		const previouslySelectedWallets = getHistoricallySelectedWallets();
		if (!previouslySelectedWallets.includes(name)) {
			previouslySelectedWallets.push(name);
			localStorage.setItem(
				HISTORICALLY_SELECTED_WALLETS_STORAGE_KEY,
				JSON.stringify(previouslySelectedWallets)
			);
		}
	}, []);

	const handleSelectWallet = useCallback(
		async (walletName: VelocityWalletName) => {
			clearPrivateBetaWalletRejected();
			handleAddToHistoricallySelectedWallets(walletName);
			setIsPrivyEmailAuth(false);
			walletConnectionState.instance.update(
				'Connecting',
				true,
				CONNECTION_STEP_LOCK_KEY
			);
			await handleSelectProvider(walletName);
		},
		[
			handleAddToHistoricallySelectedWallets,
			handleSelectProvider,
			walletConnectionState.instance,
		]
	);

	const handleLoginWithPrivyEmail = useCallback(() => {
		setIsPrivyEmailAuth(true);
		setIsOtpSent(true);
	}, []);

	const handleClose = useCallback(
		(callCallback = true) => {
			hide();

			if (callback && callCallback) {
				callback();
			}
		},
		[callback, hide]
	);

	const handleBackFromPrivyEmailOtp = () => {
		setIsPrivyEmailAuth(false);
	};

	const handleShowMoreWallets = () => {
		setIsPrivyEmailAuth(false);
		setShowMoreWallets(true);
	};

	const handleBackToMainWallets = () => {
		setShowMoreWallets(false);
	};

	// initialize the appkit wallet provider - we dynamically import this so that the appkit wallet provider is only initialized when the modal is opened (reduces initial bundle size)
	useEffect(() => {
		import('src/utils/appKitWalletSetup').then(({ setupAppKitWallet }) =>
			setupAppKitWallet()
		);
	}, []);

	// Once connected, always release onboarding's temporary connecting lock.
	useEffect(() => {
		walletConnectionState.instance.update(
			'Connecting',
			false,
			CONNECTION_STEP_LOCK_KEY
		);
	}, [connected, walletConnectionState.instance]);

	// close modal if user is connected and has already acknowledged terms
	useEffect(() => {
		if (connected && hasAccountsLoaded) {
			if (isPrivateBetaWhitelistEnabled && (isLoading || !hasAccess)) {
				return;
			}

			setHasConnectedWalletBefore(true);

			if (canGoBack) {
				goBack();
				return;
			}

			if (
				!hasAcknowledgedTerms.shouldShowTermsReminder &&
				accountExists === false
			) {
				// show deposit modal if account doesn't exist
				show({ type: 'deposit', props: {} });

				return;
			}

			handleClose();
		}
	}, [
		connected,
		hasAccess,
		hasAcknowledgedTerms.shouldShowTermsReminder,
		hasAccountsLoaded,
		accountExists,
		handleClose,
		isLoading,
		isPrivateBetaWhitelistEnabled,
		setHasConnectedWalletBefore,
		show,
		goBack,
		canGoBack,
		hide,
	]);

	const title = showMoreWallets ? 'More Wallets' : '';

	const onBack = (() => {
		if (isPrivyEmailAuth) {
			return handleBackFromPrivyEmailOtp;
		}

		if (showMoreWallets) {
			return handleBackToMainWallets;
		}

		return undefined;
	})();

	const showBack = showMoreWallets || isPrivyEmailAuth;
	const showTitle = isMobileScreenSize || showBack;

	const content = (() => {
		if (isPrivyEmailAuth) {
			return (
				<PrivyEmailOtpPage
					email={privyEmail}
					setIsOtpSent={setIsOtpSent}
					resendSecondsRemaining={otpResendSecondsRemaining}
				/>
			);
		}

		if (showMoreWallets) {
			return (
				<MoreWalletsPage
					detectedWallets={hiddenDetectedWallets}
					nonDetectedWallets={hiddenNonDetectedWallets}
					onChooseWallet={handleSelectWallet}
					onBack={handleBackToMainWallets}
				/>
			);
		}

		return (
			<MainWalletsPage
				privyEmail={privyEmail}
				setPrivyEmail={setPrivyEmail}
				wallets={walletsToShow}
				hiddenWallets={hiddenWallets}
				onChooseWallet={handleSelectWallet}
				onShowMoreWallets={handleShowMoreWallets}
				onLoginWithPrivyEmail={handleLoginWithPrivyEmail}
				privyOtpResendSecondsRemaining={otpResendSecondsRemaining}
			/>
		);
	})();

	return (
		<BaseModal.Container
			className="sm:max-h-135"
			onClose={() => handleClose(false)}
			dataTestId="connect-wallet-modal"
		>
			<BaseModal.Title
				className={cj(!showTitle && 'sr-only')}
				onClose={() => handleClose(false)}
				showBack={showBack}
				onBack={onBack}
				closeButtonTestId="connect-wallet-modal-close"
			>
				{title}
			</BaseModal.Title>
			<BaseModal.Body>{content}</BaseModal.Body>
		</BaseModal.Container>
	);
}
